import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

// Feature C — "Explain this chart" copilot channel.
//
// The PRIME Copilot drawer's open state lives in AppShell; before this there was
// no way for a page to open the drawer with a prefilled question. This lightweight
// context adds that channel without touching the existing launcher/close flow:
//
//   openWith({ question, visual, autoSubmit }) — opens the drawer and stores a
//     one-shot `prefill` (a nonce-tagged object). The drawer watches the nonce,
//     sets its input to `question`, attaches `visual` to the NEXT /ai/ask context,
//     and (when autoSubmit) sends immediately. A fresh nonce on every call means
//     re-clicking Explain re-asks.
//   close() — closes the drawer (delegates to AppShell's closeCopilot).
//   prefill — the current prefill object { question, visual, autoSubmit, nonce }.
//
// autoSubmit is prop-gated at the call site so a prefill-only mode (populate the
// input but let the user press Ask) is still expressible and testable.

const CopilotControllerContext = createContext(null);

// Safe no-op controller for trees rendered without the provider (e.g. isolated
// component tests, or any surface mounted outside AppShell). An ExplainChartButton
// there simply does nothing rather than crashing.
const NOOP_CONTROLLER = Object.freeze({
  open: false,
  prefill: null,
  openWith: () => {},
  close: () => {},
});

export function CopilotControllerProvider({ open = false, onOpen, onClose, children }) {
  const [prefill, setPrefill] = useState(null);
  const nonceRef = useRef(0);

  const openWith = useCallback(
    ({ question = '', visual = null, autoSubmit = true } = {}) => {
      nonceRef.current += 1;
      setPrefill({ question, visual, autoSubmit, nonce: nonceRef.current });
      if (typeof onOpen === 'function') onOpen();
    },
    [onOpen],
  );

  const close = useCallback(() => {
    if (typeof onClose === 'function') onClose();
  }, [onClose]);

  const value = useMemo(
    () => ({ open, prefill, openWith, close }),
    [open, prefill, openWith, close],
  );

  return (
    <CopilotControllerContext.Provider value={value}>{children}</CopilotControllerContext.Provider>
  );
}

export function useCopilotController() {
  return useContext(CopilotControllerContext) || NOOP_CONTROLLER;
}
